import React, { useState, useEffect } from 'react';
import { Navbar } from '../components/Navbar';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, TestTube2, Store as StoreIcon, X } from 'lucide-react';

export default function Stores() {
  const { user } = useAuth();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [formData, setFormData] = useState({
    marketplace: 'trendyol',
    store_name: '',
    seller_id: '',
    api_key: '',
    api_secret: '',
    mode: 'hybrid',
    active: true,
    custom_prompt: ''
  });

  useEffect(() => {
    fetchStores();
  }, []);

  const fetchStores = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/stores`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStores(data);
      }
    } catch (error) {
      console.error('Error fetching stores:', error);
      toast.error('Mağazalar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingStore
        ? `${process.env.REACT_APP_BACKEND_URL}/api/settings/stores/${editingStore.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/settings/stores`;
      
      const method = editingStore ? 'PUT' : 'POST';
      
      // For Hepsiburada, api_secret is not needed (only Mağaza ID and Servis Anahtarı)
      const submitData = { ...formData };
      if (submitData.marketplace === 'hepsiburada') {
        submitData.api_secret = '';  // Empty string for Hepsiburada
      }
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });
      
      if (response.ok) {
        toast.success(editingStore ? 'Mağaza güncellendi' : 'Mağaza eklendi');
        setShowDialog(false);
        setEditingStore(null);
        resetForm();
        fetchStores();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'İşlem başarısız');
      }
    } catch (error) {
      console.error('Error saving store:', error);
      toast.error('Bir hata oluştu');
    }
  };

  const handleEdit = (store) => {
    setEditingStore(store);
    setFormData({
      marketplace: store.marketplace,
      store_name: store.store_name,
      seller_id: store.seller_id,
      api_key: store.api_key,
      api_secret: store.api_secret,
      mode: store.mode || 'hybrid',
      active: store.active !== undefined ? store.active : true,
      custom_prompt: store.custom_prompt || ''
    });
    setShowDialog(true);
  };

  const handleDelete = async (storeId) => {
    if (!window.confirm('Bu mağazayı silmek istediğinizden emin misiniz?')) {
      return;
    }
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/stores/${storeId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        toast.success('Mağaza silindi');
        fetchStores();
      } else {
        toast.error('Silme işlemi başarısız');
      }
    } catch (error) {
      console.error('Error deleting store:', error);
      toast.error('Bir hata oluştu');
    }
  };

  const handleTestConnection = async (store) => {
    toast.info('Bağlantı test ediliyor...');
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/stores/${store.id}/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success(
          <div>
            <div className="font-bold">✅ Bağlantı Başarılı!</div>
            <div className="text-sm mt-1">{data.details || data.message}</div>
            {data.questions_count !== undefined && (
              <div className="text-xs mt-1 opacity-80">
                📊 {data.questions_count} cevaplanmamış soru bulundu
              </div>
            )}
          </div>,
          { duration: 7000 }
        );
      } else {
        toast.error(
          <div>
            <div className="font-bold">❌ Bağlantı Başarısız</div>
            <div className="text-sm mt-1">{data.error || data.message || data.detail || 'Bilinmeyen hata'}</div>
          </div>,
          { duration: 10000 }
        );
      }
    } catch (error) {
      console.error('Error testing connection:', error);
      toast.error('Test edilemedi: ' + error.message);
    }
  };

  const handleToggleMode = async (store) => {
    const newMode = store.mode === 'hybrid' ? 'full-auto' : 'hybrid';
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/stores/${store.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...store,
          mode: newMode
        })
      });
      
      if (response.ok) {
        toast.success(`Mod güncellendi: ${newMode === 'hybrid' ? 'Hibrit' : 'Full-Oto'}`);
        fetchStores();
      } else {
        toast.error('Mod güncellenemedi');
      }
    } catch (error) {
      console.error('Error toggling mode:', error);
      toast.error('Bir hata oluştu');
    }
  };

  const resetForm = () => {
    setFormData({
      marketplace: 'trendyol',
      store_name: '',
      seller_id: '',
      api_key: '',
      api_secret: '',
      mode: 'hybrid',
      active: true,
      custom_prompt: ''
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Mağazalar</h1>
              <p className="mt-1 text-sm text-gray-500">
                Pazaryeri mağazalarınızı yönetin
              </p>
            </div>
            <button
              onClick={() => {
                setEditingStore(null);
                resetForm();
                setShowDialog(true);
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-orange-600 hover:bg-orange-700"
            >
              <Plus className="w-5 h-5 mr-2" />
              Yeni Mağaza
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {stores.length === 0 ? (
          <div className="text-center py-12">
            <StoreIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Mağaza bulunamadı</h3>
            <p className="mt-1 text-sm text-gray-500">
              İlk mağazanızı ekleyerek başlayın.
            </p>
            <div className="mt-6">
              <button
                onClick={() => {
                  setEditingStore(null);
                  resetForm();
                  setShowDialog(true);
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700"
              >
                <Plus className="w-5 h-5 mr-2" />
                Mağaza Ekle
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {stores.map((store) => (
              <div key={store.id} className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center">
                    <StoreIcon className="w-8 h-8 text-orange-500" />
                    <div className="ml-3">
                      <h3 className="text-lg font-semibold text-gray-900">{store.store_name}</h3>
                      <p className="text-sm text-gray-500 capitalize">{store.marketplace}</p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                    store.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {store.active ? 'Aktif' : 'Pasif'}
                  </span>
                </div>

                <div className="mt-4 space-y-2">
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Satıcı ID:</span> {store.seller_id}
                  </p>
                  
                  {/* Mode Toggle */}
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm font-medium text-gray-700">Çalışma Modu:</span>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleToggleMode(store)}
                        className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                          store.mode === 'hybrid'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-200 text-gray-600'
                        }`}
                      >
                        Hibrit
                      </button>
                      <button
                        onClick={() => handleToggleMode(store)}
                        className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                          store.mode === 'full-auto'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-200 text-gray-600'
                        }`}
                      >
                        Full-Oto
                      </button>
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex space-x-2">
                  <button
                    onClick={() => handleEdit(store)}
                    className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Düzenle
                  </button>
                  <button
                    onClick={() => handleTestConnection(store)}
                    className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <TestTube2 className="w-4 h-4 mr-1" />
                    Test
                  </button>
                  <button
                    onClick={() => handleDelete(store.id)}
                    className="inline-flex justify-center items-center px-3 py-2 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      {showDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingStore ? 'Mağazayı Düzenle' : 'Yeni Mağaza Ekle'}
                </h2>
                <button
                  onClick={() => {
                    setShowDialog(false);
                    setEditingStore(null);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Platform *
                  </label>
                  <select
                    value={formData.marketplace}
                    onChange={(e) => setFormData({ ...formData, marketplace: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  >
                    <option value="trendyol">Trendyol</option>
                    <option value="hepsiburada" disabled>Hepsiburada (Yakında)</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">Platform değiştirilemez</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mağaza Adı *
                  </label>
                  <input
                    type="text"
                    value={formData.store_name}
                    onChange={(e) => setFormData({ ...formData, store_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="Örn: KB"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {formData.marketplace === 'trendyol' ? 'Satıcı ID *' : 'Mağaza ID *'}
                  </label>
                  <input
                    type="text"
                    value={formData.seller_id}
                    onChange={(e) => setFormData({ ...formData, seller_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder={formData.marketplace === 'trendyol' ? 'Örn: 123456' : 'Örn: dbecf6ee-beed-4fcb-bead-19741737e4ca'}
                    required
                  />
                  {formData.marketplace === 'hepsiburada' && (
                    <p className="text-xs text-gray-500 mt-1">Hepsiburada Satıcı Paneli > Entegrasyon > Mağaza ID</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {formData.marketplace === 'trendyol' ? 'API Key *' : 'Servis Anahtarı *'}
                  </label>
                  <input
                    type="text"
                    value={formData.api_key}
                    onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder={formData.marketplace === 'trendyol' ? 'Örn: nB1u2KpXn8P9p1S16UqwqYG8H6s3QAr0' : 'Örn: FFj9gg1AHuyR'}
                    required
                  />
                  {formData.marketplace === 'hepsiburada' && (
                    <p className="text-xs text-gray-500 mt-1">Hepsiburada Satıcı Paneli > Entegrasyon > Servis Anahtarı</p>
                  )}
                </div>

                {formData.marketplace === 'trendyol' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      API Secret *
                    </label>
                    <input
                      type="password"
                      value={formData.api_secret}
                      onChange={(e) => setFormData({ ...formData, api_secret: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="Örn: ••••••••••••••••••"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Trendyol API Secret Key</p>
                  </div>
                )}
                
                {formData.marketplace === 'hepsiburada' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                    <p className="text-sm text-blue-700">
                      ℹ️ Hepsiburada için sadece Mağaza ID ve Servis Anahtarı yeterlidir.
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Çalışma Modu
                  </label>
                  <div className="flex space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="hybrid"
                        checked={formData.mode === 'hybrid'}
                        onChange={(e) => setFormData({ ...formData, mode: e.target.value })}
                        className="mr-2"
                      />
                      <span className="text-sm">Hibrit (Onay Gerekli)</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="full-auto"
                        checked={formData.mode === 'full-auto'}
                        onChange={(e) => setFormData({ ...formData, mode: e.target.value })}
                        className="mr-2"
                      />
                      <span className="text-sm">Full-Oto (Otomatik Gönder)</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    AI Yanıt Promptu (Opsiyonel)
                  </label>
                  <textarea
                    value={formData.custom_prompt}
                    onChange={(e) => setFormData({ ...formData, custom_prompt: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    rows="3"
                    placeholder="Bu mağaza için özel prompt..."
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Boş bırakılırsa genel prompt kullanılır. Bu mağaza için özel bir yanıt tarzı belirleyebilirsiniz.
                  </p>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowDialog(false);
                      setEditingStore(null);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700"
                  >
                    {editingStore ? 'Güncelle' : 'Ekle'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
